import { FGTypography, FGStack, FGTile, FGIcons, FGStackItem } from "@fg/design-system";
import { drop, first, map, size } from "lodash/fp";
import React, { ReactNode, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import EventsFeedDataProvider from 'components/eventsFeed/EventFeedDataProvider';
import { IActiveCountry, IActiveCountryMap, IActiveGroup, VIEW } from "interfaces";
import cx from 'classnames';
import { useCurrentView } from "hooks/useCurrentView";
import PickerToggler, { PICKER_GAP } from "./PickerToggler";
import { inject } from "mobx-react";
import NewCountryPicker, { NewCountryPickerFill } from 'components/NewCountryPicker/NewCountryPicker';
import rootStore, { IMobxRootState } from '../../RootStore';
import { MultiCountryPickerPreview } from "./MultiCountryPickerPreview";
import SingleRiskPickerPreview from "./SingleRiskPickerPreview";
import DatePickerContainer from "./DatePickerContainer";
import { RiskPickerContingent } from "components/RiskPicker/RiskPickerContingent";
import { PickerDrawer } from "./PickerDrawer";
import { usePopupTopOffset } from 'hooks/usePopupTopOffset';
import { useViewUrlSync } from 'services/urlState/useViewUrlSync';
import { bilateralSchema } from 'services/urlState/viewSchemas';

type Props = {
    licensedCountriesArr?: IActiveCountry[];
    firstCountry?: number;
    groups?: IActiveGroup[];
    activeGroupId?: string;
    countries?: IActiveCountryMap;
    selectedCountries?: number[];
};

export const MULTILATERAL_VIEWS = [
    { path: VIEW.CONTINGENT_GRAPH, label: 'Line Graph', icon: 'Curves' },
    { path: VIEW.SORT_BY_CONTINGENT_RISK, label: 'Sort By Risk', icon: 'Dots' },
    {
        path: VIEW.SORT_BY_CONTINGENT_DELTA,
        label: 'Sort By Delta',
        icon: 'Triangle',
    },
];

export const MultilateralRiskPicker = inject(
    ({ countryStore, settingsStore }: IMobxRootState) => {
        return {
            licensedCountriesArr: countryStore.countries.licensedCountriesArr,
            firstCountry: first(countryStore.currentCountryList),
            groups: settingsStore.activeGroups,
            activeGroupId: settingsStore.activeGroupId,
            countries: countryStore.countries.countries,
            selectedCountries: countryStore.currentCountryList,
        };
    }
)((props: Props) => {
    const {
        licensedCountriesArr,
        firstCountry,
        countries,
        selectedCountries,
        groups,
    } = props;
    const navigate = useNavigate();
    const currentView = useCurrentView();

    const riskPickerRef = useRef(null)
    const secondaryCountries = useMemo(() => drop(1, selectedCountries), [
        selectedCountries,
    ]);
    const [primaryTogglerRef, primaryPopupTop] = usePopupTopOffset();
    const [secondaryTogglerRef, secondaryPopupTop] = usePopupTopOffset();
    return (
        <FGStack direction="column" style={{ width: '100%' }}>
            <FGStackItem>
                <FGStack gap={PICKER_GAP} direction="column">
                    <FGStackItem>
                        <FGTypography
                            tag="label"
                            variant="label2"
                            color="strong">
                            Primary Country
                        </FGTypography>
                    </FGStackItem>
                    <FGStackItem>
                        <div ref={primaryTogglerRef}>
                            <PickerToggler
                                label={
                                    countries[firstCountry]?.name ||
                                    'Select Country'
                                }
                                icon="Navigator">
                                <NewCountryPickerFill top={primaryPopupTop}>
                                    <NewCountryPicker
                                        groups={groups}
                                        canSelectAllCountries={false}
                                        countriesList={licensedCountriesArr}
                                        selectedCountriesList={[firstCountry]}
                                        isMultiSelect={false}
                                        onCountrySelected={(countryId: number) => {
                                            rootStore.countryStore.changeFirstCountry(
                                                countryId
                                            );
                                        }}
                                    />
                                </NewCountryPickerFill>
                            </PickerToggler>
                        </div>
                    </FGStackItem>
                </FGStack>
            </FGStackItem>
            <FGStackItem>
                <FGStack gap={PICKER_GAP} direction="column">
                    <FGStackItem>
                        <FGTypography
                            tag="label"
                            variant="label2"
                            color="strong">
                            Comparison Countries
                        </FGTypography>
                    </FGStackItem>
                    <FGStackItem>
                        <div ref={secondaryTogglerRef}>
                            <PickerToggler
                                alignItems={
                                    size(secondaryCountries) >= 1
                                        ? 'flex-start'
                                        : 'center'
                                }
                                labelComponent={
                                    <MultiCountryPickerPreview
                                        selectedCountries={secondaryCountries}
                                        onCountryClick={countryId => {
                                            rootStore.countryStore.toggleSecondaryCountry(
                                                countryId
                                            );
                                        }}
                                    />
                                }
                                icon="Navigator">
                                <NewCountryPickerFill top={secondaryPopupTop}>
                                    <NewCountryPicker
                                        groups={groups}
                                        canSelectAllCountries={false}
                                        countriesList={licensedCountriesArr}
                                        selectedCountriesList={secondaryCountries}
                                        isMultiSelect={true}
                                        onCountrySelected={(countryId: number) => {
                                            rootStore.countryStore.toggleSecondaryCountry(
                                                countryId
                                            );
                                        }}
                                    />
                                </NewCountryPickerFill>
                            </PickerToggler>
                        </div>
                    </FGStackItem>
                </FGStack>
            </FGStackItem>
            <FGStackItem>
                <FGStack gap={PICKER_GAP} direction="column">
                    <FGStackItem>
                        <FGTypography
                            tag="label"
                            variant="label2"
                            color="strong">
                            Feature
                        </FGTypography>
                    </FGStackItem>
                    <FGStackItem>
                        <FGStack gap={8} direction="column">
                            {map(
                                view => (
                                    <FGTile
                                        data-testid="analytics-view-option"
                                        key={view.path}
                                        className={cx('button', {
                                            active: view.path === currentView,
                                        })}
                                        padding="small"
                                        appearance="secondary"
                                        onClick={() =>
                                            navigate(`/${view.path}`)
                                        }>
                                        <FGStack gap={8}>
                                            {/* @ts-ignore*/}
                                            <FGIcons icon={view.icon} />
                                            <FGTypography
                                                tag="label"
                                                variant="label2">
                                                {view.label}
                                            </FGTypography>
                                        </FGStack>
                                    </FGTile>
                                ),
                                MULTILATERAL_VIEWS
                            )}
                        </FGStack>
                    </FGStackItem>
                </FGStack>
            </FGStackItem>
            <FGStackItem>
                <FGStack gap={PICKER_GAP} direction="column">
                    <FGStackItem>
                        <FGTypography
                            tag="label"
                            variant="label2"
                            color="strong">
                            Date
                        </FGTypography>
                    </FGStackItem>
                    <FGStackItem>
                        <DatePickerContainer currentView={currentView} />
                    </FGStackItem>
                </FGStack>
            </FGStackItem>
            <FGStackItem>
                <FGStack gap={PICKER_GAP} direction="column">
                    <FGStackItem>
                        <FGTypography
                            tag="label"
                            variant="label2"
                            color="strong">
                            Bilateral Risk Type
                        </FGTypography>
                    </FGStackItem>
                    <FGStackItem>
                        <PickerToggler
                            ref={riskPickerRef}
                            labelComponent={<SingleRiskPickerPreview />}
                            icon="Charts"
                            collapsedIcon="RightArrow">
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                }}>
                                <RiskPickerContingent
                                    onClose={() =>
                                        riskPickerRef.current.close()
                                    }
                                />
                            </div>
                        </PickerToggler>
                    </FGStackItem>
                </FGStack>
            </FGStackItem>
        </FGStack>
    );
});


type MultilateralRiskPageProps = {
    children: ReactNode;
}
export const MultilateralRiskPage = React.memo(
    (props: MultilateralRiskPageProps) => {
        useViewUrlSync(bilateralSchema);
        return (
            <FGStack style={{ height: '100%' }}>
                <PickerDrawer
                    title="Bilateral Risks"
                    side="right"
                    defaultOpen>
                    <MultilateralRiskPicker />
                </PickerDrawer>
                <div
                    style={{
                        flex: 1,
                    }}>
                    {props.children}
                </div>
                <EventsFeedDataProvider />
            </FGStack>
        );
    }
);